import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { css } from '@emotion/react';
import { gsap, ScrollTrigger } from 'gsap/all';
import { fullHeight, useHsHeader } from '../../util/hsHeader';
import FullScreenVideo from '../FullScreenVideo';
import { useDeviceSize } from '../../util/useDeviceSize';
import { useStory } from '../StoryContent';
import VideoMobile from '../VideoMobile';
import { P } from '../../styles/typography';
import AudioPlayer, { AudioPlayerMobile } from '../AudioPlayer';
import {
  AnimatedTextWrapper,
  centered,
  MobileBlockWrapper,
} from '../../styles/helpers';
import { FadeInAnimation } from '../../effects/withAnimation';

gsap.registerPlugin(ScrollTrigger);

export default function VideoSlide8({ video, images, copy, audio }) {
  const [{ headerHeight }] = useHsHeader();
  const { toggleVideo } = useStory();
  const { isWideScreen } = useDeviceSize();
  const [isAudioFocused, setIsAudioFocused] = useState(false);

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const videoTriggerRef = useRef();
  const videoRef = useRef();
  const imageRef = useRef();
  const imageHolderRef = useRef();
  const audioRef = useRef();
  const copyRef = useRef();

  useEffect(() => {
    if (!isWideScreen) {
      return undefined;
    }

    const tl = gsap
      .timeline()

      .to(videoTriggerRef.current, {
        duration: 0.2,
        x: 0,
        ease: 'ease-in',
      })
      .add(() => {
        setIsVideoPlaying(v => !v);
      })
      .to(videoTriggerRef.current, {
        duration: 0.4,
      })
      .add(() => {
        setIsVideoPlaying(v => !v);
      })
      .to(copyRef.current, {
        duration: 0.2,
        y: 0,
      })
      .to(imageHolderRef.current, {
        duration: 0.4,
        delay: -0.1,
        y: 0,
      })
      .to(imageRef.current, {
        duration: 0.4,
        delay: -0.4,
        y: '-100%',
      })
      .to(videoRef.current, {
        duration: 0.1,
        delay: -0.3,
        opacity: 0,
      })
      .to(audioRef.current, {
        duration: 0.2,
        delay: -0.2,
        y: 0,
      })

      .to(audioRef.current, {
        css: {
          pointerEvents: 'auto',
          opacity: 1,
        },
        delay: -0.1,
        duration: 0.01,
      })
      .add(() => {
        setIsAudioFocused(v => !v);
      })
      .to(videoTriggerRef.current, {
        duration: 0.2,
      })
      .to(copyRef.current, {
        duration: 0.2,
        x: '-100%',
      })
      .add(() => {
        setIsAudioFocused(v => !v);
      })
      .to(audioRef.current, {
        x: '-50%',
        duration: 0.2,
      })
      .to(audioRef.current, {
        css: {
          pointerEvents: 'none',
          opacity: 0,
        },
        delay: -0.1,
        duration: 0.01,
      });

    const st = ScrollTrigger.create({
      animation: tl,
      trigger: videoTriggerRef.current,
      pin: true,
      pinSpacing: false,
      scrub: true,
      start: 'top top',
      end: `bottom 0`,
      anticipatePin: 1,
      onToggle: ({ isActive }) => {
        videoTriggerRef.current.style.pointerEvents = isActive
          ? 'auto'
          : 'none';
      },
    });

    return () => {
      tl.kill();
      st.kill();
    };
  }, [headerHeight, toggleVideo, video, isWideScreen]);

  return isWideScreen ? (
    <div
      css={css`
        ${fullHeight(7)};
        position: relative;
        z-index: 1;
        transform: translateY(-100vh) translateX(100%);
        pointer-events: none;
      `}
      ref={videoTriggerRef}
    >
      <FullScreenVideo
        videoId={video.id}
        pause={!isVideoPlaying}
        ref={videoRef}
      />
      <div
        ref={imageHolderRef}
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          ${fullHeight()}
          transform: translateY(100%);
          pointer-events: none;
        `}
      >
        <img
          ref={imageRef}
          src={images[0].src}
          css={css`
            width: 100%;
          `}
          alt=""
        />
      </div>
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          ${fullHeight()}
          pointer-events: none;
          display: flex;
          justify-content: flex-end;
          align-items: center;
          transform: translateY(100%);
        `}
        ref={copyRef}
      >
        <P
          css={css`
            width: 70%;
            padding-right: 5%;
            transform: translateY(-100%);
          `}
        >
          {copy[0].text}
        </P>
      </div>
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          ${fullHeight()}
          display: flex;
          align-items: center;
          transform: translateY(100%);
          opacity: 0;
          transition: opacity 0.2s ease-in;
          pointer-events: none;
        `}
        ref={audioRef}
      >
        <div
          css={css`
            width: 30%;
            ${centered}
          `}
        >
          <AudioPlayer url={audio.url} isFocused={isAudioFocused} />
        </div>
      </div>
    </div>
  ) : (
    <div>
      <MobileBlockWrapper>
        <img src={images[0].src} alt="" />
      </MobileBlockWrapper>

      <AnimatedTextWrapper>
        <P mobile>{copy[0].text}</P>
      </AnimatedTextWrapper>

      <MobileBlockWrapper
        css={css`
          display: flex;
          justify-content: center;
        `}
      >
        <FadeInAnimation>
          <AudioPlayerMobile url={audio.url} />
        </FadeInAnimation>
      </MobileBlockWrapper>
      <VideoMobile videoId={video.id} />
    </div>
  );
}

VideoSlide8.propTypes = {
  video: PropTypes.shape({ id: PropTypes.number }),
  audio: PropTypes.shape({ url: PropTypes.string }),
  images: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string })),
  copy: PropTypes.arrayOf(PropTypes.shape({ text: PropTypes.node })),
};

VideoSlide8.defaultProps = {
  copy: null,
  audio: null,
  video: null,
  images: null,
};
